<script setup>
import { useCommonCodeStore } from '@/store/commoncode-store';
import { useReservationsStore } from '@/store/reservation-store';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps({
    modelValue: { type: Object, required: true },
    contractType: { type: String, default: 'SHORT' },
    validMsg: { type: Object, default: () => ({}) },
    requiredList: { type: [Array, Object], default: () => [] },
    readonlyList: { type: [Array, Object], default: () => [] },
    hideList: { type: [Array, Object], default: () => [] },
    idPrefix: { type: String, default: '' }
});

const rentalMonth = ref(0);
const route = useRoute();
const store = useReservationsStore();
const sessionKey = `${route.name}`;

const emit = defineEmits(['update:modelValue', 'update', 'update:rentalMonth']);

const model = computed({
    get: () => props.modelValue,
    set: (v) => emit('update:modelValue', v)
});

// const mappedFiles = computed(() => {
//     if (!model.value.contract_attachment_list) return [];
//     return model.value.contract_attachment_list.map((item) => ({
//         fileid: item.attachment_id,
//         filename: item.attachment_filename,
//         name: item.original_filename,
//         uploaded: true,
//         delete: false
//     }));
// });

const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);
const commonCode = (code) => computed(() => commonCodes.value[code]);

const selectedDate = computed(() => ({
    pickupDate: model.value.pickup_date,
    pickupTime: model.value.pickup_time,
    returnExpectedDate: model.value.return_expected_date,
    returnExpectedTime: model.value.return_expected_time
}));

const allFormFields = [
    'contract_no',
    'vehicle_no',
    'vehicle_model_name',
    'vehicle_group_id',
    'fuel_type',
    'pickup_date',
    'pickup_time',
    'return_expected_date',
    'return_expected_time',
    'pickup_delivery_id',
    'pickup_delivery_address',
    'pickup_delivery_address_detail',
    'return_expected_delivery_id',
    'return_delivery_address',
    'return_delivery_address_detail',
    'garage_id',
    'pickup_fuel_gauge',
    'pickup_mileage',
    'return_fuel_gauge',
    'return_mileage',
    'vehicle_image'
];

const allReadonly = ref(false);
const setAllReadonly = (val = true) => {
    allReadonly.value = !!val;
};

const toSet = (src) => {
    if (Array.isArray(src)) return new Set(src);
    if (src && typeof src === 'object') return new Set(Object.keys(src).filter((k) => !!src[k]));
    return new Set();
};
const readonlySet = computed(() => toSet(props.readonlyList));
const hideSet = computed(() => toSet(props.hideList));
const isFieldReadonly = (key) => allReadonly.value || readonlySet.value.has(key);
const isHidden = (key) => hideSet.value.has(key);

const baseRequired = ['vehicle_no', 'pickup_date', 'return_expected_date', 'garage_id'];
const requiredKeys = computed(() => {
    const r = props.requiredList;
    if (Array.isArray(r) && r.length) return r;
    if (r && typeof r === 'object' && Object.keys(r).length) return Object.keys(r).filter((k) => !!r[k]);
    return baseRequired;
});
const requiredKeySet = computed(() => new Set(requiredKeys.value));
const isEffectivelyRequired = (key) => requiredKeySet.value.has(key) && !isFieldReadonly(key) && !isHidden(key);

const initialValidMessage = {
    contract_no: '',
    vehicle_no: '',
    vehicle_model_name: '',
    vehicle_group_id: '',
    fuel_type: '',
    pickup_date: '',
    pickup_time: '',
    return_expected_date: '',
    return_expected_time: '',
    pickup_delivery_address_detail: '',
    pickup_delivery_address: '',
    return_delivery_address: '',
    return_delivery_address_detail: '',
    garage_id: '',
    pickup_fuel_gauge: '',
    pickup_mileage: '',
    return_fuel_gauge: '',
    return_mileage: ''
};
const validationMessage = ref({ ...initialValidMessage });

const setErrorMessage = (payload, reset = false) => {
    if (!payload) return;
    if (reset) Object.keys(validationMessage.value).forEach((k) => (validationMessage.value[k] = ''));
    const collect = (node, out = []) => {
        if (node == null) return out;
        if (Array.isArray(node)) {
            node.forEach((n) => collect(n, out));
            return out;
        }
        if (typeof node === 'object') {
            for (const [k, v] of Object.entries(node)) v && typeof v === 'object' && !Array.isArray(v) ? collect(v, out) : out.push([k, v]);
            return out;
        }
        return out;
    };
    for (const [rawKey, rawVal] of collect(payload)) {
        const key = String(rawKey).replace(/^contract_data\./, '');
        if (!(key in validationMessage.value)) continue;
        let msg = Array.isArray(rawVal) ? rawVal.filter(Boolean).join(', ') : (rawVal ?? '');
        validationMessage.value[key] = String(msg);
    }
};

const checkRequired = () => {
    const m = model.value || {};
    const errors = {};

    for (const k of requiredKeySet.value) {
        if (isFieldReadonly(k) || isHidden(k)) continue;
        const val = m[k];
        const empty = val === '' || val == null;
        const idInvalid = k.endsWith('_id') && (!val || Number(val) === 0);
        if (empty || idInvalid) errors[k] = '필수 입력입니다.';
    }
    if (m.pickup_delivery_id && !m.pickup_delivery_address && !isFieldReadonly('pickup_delivery_address') && !isHidden('pickup_delivery_address')) {
        errors['pickup_delivery_address'] = '필수 입력입니다.';
    }
    if (m.return_expected_delivery_id && !m.return_delivery_address && !isFieldReadonly('return_delivery_address') && !isHidden('return_delivery_address')) {
        errors['return_delivery_address'] = '필수 입력입니다.';
    }

    setErrorMessage(errors, true);
    return { ok: Object.keys(errors).length === 0, errors };
};

const parseDateFlex = (v) => {
    if (!v) return new Date('Invalid');
    if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
    const s = String(v).trim();
    const digits = s.replace(/\D/g, '');
    if (digits.length === 8) {
        const y = Number(digits.slice(0, 4));
        const m = Number(digits.slice(4, 6));
        const d = Number(digits.slice(6, 8));
        return new Date(y, m - 1, d);
    }
    const dt = new Date(s);
    return dt;
};

const normalizeDateStr = (v) => {
    const dt = parseDateFlex(v);
    if (isNaN(dt)) return '';
    return formatToYyyyMmDd(dt);
};

const formatToYyyyMmDd = (date) => {
    const y = date.getFullYear(),
        m = String(date.getMonth() + 1).padStart(2, '0'),
        d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const addCalendarMonths = (dateObj, months) => {
    const targetMonth = dateObj.getMonth() + months;
    const targetLastDay = new Date(dateObj.getFullYear(), targetMonth + 1, 0).getDate();
    const targetDay = Math.min(dateObj.getDate(), targetLastDay);

    return new Date(
        dateObj.getFullYear(),
        targetMonth,
        targetDay,
        dateObj.getHours(),
        dateObj.getMinutes(),
        dateObj.getSeconds(),
        dateObj.getMilliseconds()
    );
};

const toDateOnly = (dateObj) => new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());

const isSameDate = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const diffCalendarMonths = (start, end) => {
    const startDate = toDateOnly(start);
    const endDate = toDateOnly(end);
    let months = (endDate.getFullYear() - startDate.getFullYear()) * 12 + endDate.getMonth() - startDate.getMonth();
    if (months <= 0) return 0;
    if (addCalendarMonths(startDate, months) > endDate) months--;
    return Math.max(months, 0);
};

const calculateDate = (base, { days = 0, months = 0, years = 0 }) => {
    let dt = parseDateFlex(base);
    if (isNaN(dt)) return '';
    dt.setDate(dt.getDate() + days);
    if (months || years) dt = addCalendarMonths(dt, months + years * 12);
    return formatToYyyyMmDd(dt);
};

const minReturnDate = computed(() => {
    const base = model.value.pickup_date;
    switch (props.contractType) {
        case 'SHORT':
        case 'INSU':
            return normalizeDateStr(base);
        case 'MID':
            return calculateDate(base, { months: 1 });
        case 'LONG':
            return calculateDate(base, { years: 1 });
        default:
            return '';
    }
});

const maxReturnDate = computed(() => {
    const base = model.value.pickup_date;
    switch (props.contractType) {
        case 'INSU':
            return calculateDate(base, { days: 30 });
        default:
            return '';
    }
});

const getDefaultReturnDate = (pickupDate) => {
    switch (props.contractType) {
        case 'SHORT':
            return calculateDate(pickupDate, { days: 1 });
        case 'INSU':
            return calculateDate(pickupDate, { days: 7 });
        case 'MID':
            return calculateDate(pickupDate, { months: 1 });
        case 'LONG':
            return calculateDate(pickupDate, { months: 12 });
        default:
            return '';
    }
};

const setField = (field, value) => {
    const next = { ...model.value, [field]: value };

    if (field === 'pickup_date') {
        next.return_expected_date = getDefaultReturnDate(value);
        next.return_expected_time = next.pickup_time || '00:00';
    }

    if (field === 'pickup_time') {
        next.return_expected_time = value;
    }

    emit('update:modelValue', next);
};

const updateInputValue = (val) => {
    if (!val || typeof val !== 'object') return;

    store.update(sessionKey, {
        vehicle_id: val.vehicle_id ? Number(val.vehicle_id) : 0
    });

    const next = { ...model.value };

    if ('value' in val && val.value != null) next.vehicle_no = val.value;
    if ('model' in val && val.model != null) next.vehicle_model_name = val.model;
    if ('fuel_type' in val && val.fuel_type != null) next.fuel_type = val.fuel_type;
    if ('vehicle_group_id' in val && val.vehicle_group_id != null) next.vehicle_group_id = Number(val.vehicle_group_id);

    emit('update:modelValue', next);
    emit('update', next);
};

function handleAddressCompleted(inputId, { address, detailAddress }) {
    emit('update:modelValue', {
        ...model.value,
        [`${inputId}_address`]: address,
        [`${inputId}_address_detail`]: detailAddress
    });
}

const plusReturnDateTime = (days) => {
    const pick = parseDateFlex(model.value.pickup_date);
    if (isNaN(pick)) return;

    const base = parseDateFlex(model.value.return_expected_date || model.value.pickup_date);
    if (isNaN(base)) return;

    base.setDate(base.getDate() + days);

    const capStr = maxReturnDate.value;
    const cap = capStr ? parseDateFlex(capStr) : null;

    const nextDateObj = cap && !isNaN(cap) && base > cap ? cap : base;

    emit('update:modelValue', {
        ...model.value,
        return_expected_date: formatToYyyyMmDd(nextDateObj),
        return_expected_time: model.value.pickup_time
    });
};

const plusContractMonth = (monthsToAdd) => {
    let minMonths = 0;

    switch (props.contractType) {
        case 'MID':
            minMonths = 1;
            break;
        case 'LONG':
            minMonths = 12;
            break;
        default:
            return;
    }

    const months = Math.max(monthsToAdd, minMonths);

    const newReturnDate = calculateDate(model.value.pickup_date, { months });

    model.value.return_expected_date = normalizeDateStr(newReturnDate);
    model.value.return_expected_time = model.value.pickup_time;
};

const clearDateTime = () => {
    const now = new Date();
    const hhmm = now.toTimeString().slice(0, 5);
    const today = formatToYyyyMmDd(now);

    const plus = props.contractType === 'SHORT' ? { days: 1 } : props.contractType === 'INSU' ? { days: 7 } : props.contractType === 'MID' ? { months: 1 } : props.contractType === 'LONG' ? { months: 12 } : { days: 1 };

    const ret = calculateDate(today, plus);

    emit('update:modelValue', {
        ...model.value,
        pickup_date: today,
        pickup_time: hhmm,
        return_expected_date: ret,
        return_expected_time: hhmm
    });
};
const isValidTime = (t) => /^\d{2}:\d{2}$/.test(t);

const rentalDuration = computed(() => {
    const { pickup_date, return_expected_date, pickup_time, return_expected_time } = model.value;
    if (!pickup_date || !return_expected_date) return '';

    const pDate = normalizeDateStr(pickup_date);
    const rDate = normalizeDateStr(return_expected_date);
    if (!pDate || !rDate) return '';

    const st = new Date(`${pDate}T${isValidTime(pickup_time) ? pickup_time : '00:00'}`);
    const en = new Date(`${rDate}T${isValidTime(return_expected_time) ? return_expected_time : '00:00'}`);
    if (isNaN(st) || isNaN(en) || en < st) return '';

    const months = diffCalendarMonths(st, en);
    let tmp = addCalendarMonths(st, months);
    if (isSameDate(tmp, en) && tmp > en) tmp = new Date(en);

    rentalMonth.value = en > st ? Math.max(months, 1) : 0;

    const ms = en - tmp;
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    return [months ? `${months}개월` : null, days ? `${days}일` : null, hours ? `${hours}시간` : null].filter(Boolean).join(' ');
});

const isPickupAddressReadonly = computed(() => {
    validationMessage.value['pickup_delivery_address'] = '';
    const v = model.value.pickup_delivery_id;
    return v === 0 || v === '' || v == null;
});
const isReturnAddressReadonly = computed(() => {
    validationMessage.value['return_delivery_address'] = '';
    const v = model.value.return_expected_delivery_id;
    return v === 0 || v === '' || v == null;
});
const isDateEditReadonly = computed(() => isFieldReadonly('pickup_date') || isFieldReadonly('return_expected_date'));
const isPickupAddressRequired = computed(() => !isPickupAddressReadonly.value && !isFieldReadonly('pickup_delivery_address'));
const isReturnAddressRequired = computed(() => !isReturnAddressReadonly.value && !isFieldReadonly('return_delivery_address'));

const vehicleImageFromAttachments = computed(function () {
    if (!model.value.contract_attachment_list || !Array.isArray(model.value.contract_attachment_list)) {
        return model.value.vehicle_image || [];
    }

    var attachmentImages = model.value.contract_attachment_list.map(function (item) {
        return {
            fileid: item.attachment_id,
            filename: item.attachment_filename,
            name: item.original_filename,
            uploaded: true,
            delete: false
        };
    });

    var existingImages = Array.isArray(model.value.vehicle_image) ? model.value.vehicle_image : [];
    var combined = existingImages.concat(attachmentImages);

    var unique = [];
    for (var i = 0; i < combined.length; i++) {
        var cur = combined[i] || {};
        var dup = false;

        for (var j = 0; j < unique.length; j++) {
            var it = unique[j] || {};

            if (cur.uploaded && it.uploaded) {
                if (cur.fileid === it.fileid && cur.filename === it.filename) {
                    dup = true;
                    break;
                }
            } else if (!cur.uploaded && !it.uploaded) {
                var curSize = cur.size != null ? String(cur.size) : '';
                var itSize = it.size != null ? String(it.size) : '';
                if (cur.name === it.name && curSize === itSize) {
                    dup = true;
                    break;
                }
            }
        }

        if (!dup) unique.push(cur);
    }

    return unique;
});

watch(
    () => props.validMsg,
    (nv) => setErrorMessage(nv, true),
    { immediate: true }
);

watch(
    rentalMonth,
    (month) => {
        emit('update:rentalMonth', month);
    },
    { immediate: true }
);

defineExpose({ setAllReadonly, checkRequired, getHiddenKeys: () => Array.from(hideSet.value) });
</script>

<template>
    <div class="section-box base-text">
        <h2 class="section-title py-4">차량 정보</h2>

        <div class="section-body">
            <div class="section-left">
                <div class="section-row items-center">
                    <div class="input-box w-[40%]">
                        <DatePickerWithRegistComponent2
                            :inputid="`${idPrefix}_pickup_date`"
                            :required="isEffectivelyRequired('pickup_date')"
                            :readonly="isFieldReadonly('pickup_date')"
                            :errmsg="validationMessage.pickup_date"
                            :modelValue="model.pickup_date"
                            @update:modelValue="(v) => setField('pickup_date', v)"
                            label="배차일시"
                        />
                    </div>
                    <div class="input-box w-[30%]">
                        <timeonly
                            :inputid="`${idPrefix}_pickup_time`"
                            :placeholder="'00:00'"
                            :errmsg="validationMessage.pickup_time"
                            :readonly="isFieldReadonly('pickup_time')"
                            :modelValue="model.pickup_time"
                            @update:modelValue="(v) => setField('pickup_time', v)"
                        />
                    </div>
                    <div class="flex w-[30%] justify-between text-[0.9rem] mt-[2.2rem]">
                        <button class="rounded-[0.4rem] text-[#0038ea]" @click="clearDateTime()">
                            <i v-if="!isDateEditReadonly" class="pi pi-undo"></i>
                        </button>
                        <p class="select-none">{{ rentalDuration || '배차기간을 선택하세요.' }}</p>
                    </div>
                </div>

                <div class="section-row items-center">
                    <div class="input-box w-[40%]">
                        <DatePickerWithRegistComponent2
                            :inputid="`${idPrefix}_return_expected_date`"
                            :required="isEffectivelyRequired('return_expected_date')"
                            :errmsg="validationMessage.return_expected_date"
                            label="반납일시"
                            :readonly="isFieldReadonly('return_expected_date')"
                            :modelValue="model.return_expected_date"
                            @update:modelValue="(v) => setField('return_expected_date', v)"
                        />
                    </div>
                    <div class="input-box w-[30%]">
                        <timeonly
                            :inputid="`${idPrefix}_return_expected_time`"
                            :placeholder="'00:00'"
                            :errmsg="validationMessage.return_expected_time"
                            :readonly="isFieldReadonly('return_expected_time')"
                            :modelValue="model.return_expected_time"
                            @update:modelValue="(v) => setField('return_expected_time', v)"
                        />
                    </div>
                    <div v-if="(contractType === 'SHORT' || contractType === 'INSU') && !isDateEditReadonly" class="flex items-center mt-8 w-[30%] gap-2 text-sm">
                        <button @click="plusReturnDateTime(1)" class="flex-1 h-[2.5rem] rounded-md border border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150 font-semibold">+ 1일</button>
                        <button @click="plusReturnDateTime(7)" class="flex-1 h-[2.5rem] rounded-md border border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150 font-semibold">+ 7일</button>
                        <button @click="plusReturnDateTime(10)" class="flex-1 h-[2.5rem] rounded-md border border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150 font-semibold">+ 10일</button>
                    </div>
                    <div v-else-if="!isDateEditReadonly" class="flex items-center mt-8 w-[30%] gap-2 text-sm">
                        <button @click="plusContractMonth(1)" class="flex-1 h-[2.5rem] rounded-md border border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150 font-semibold">+ 1개월</button>
                        <button @click="plusContractMonth(6)" class="flex-1 h-[2.5rem] rounded-md border border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150 font-semibold">+ 6개월</button>
                        <button @click="plusContractMonth(12)" class="flex-1 h-[2.5rem] rounded-md border border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150 font-semibold">+ 1년</button>
                    </div>
                </div>

                <div class="section-row">
                    <div class="input-box w-[40%]">
                        <vehicle
                            :inputid="`${idPrefix}_vehicle_no`"
                            :required="isEffectivelyRequired('vehicle_no')"
                            :placeholder="'차량을 선택하세요'"
                            :errmsg="validationMessage.vehicle_no"
                            label="차량번호"
                            :readonly="isFieldReadonly('vehicle_no')"
                            :modelValue="model.vehicle_no"
                            @update:modelValue="(v) => setField('vehicle_no', v)"
                            @input="updateInputValue($event)"
                            :selectedDate="selectedDate"
                        />
                    </div>
                    <div class="input-box w-[30%]">
                        <inputbox
                            :inputid="`${idPrefix}_vehicle_model_name`"
                            :errmsg="validationMessage.vehicle_model_name"
                            :readonly="true"
                            label="모델"
                            :modelValue="model.vehicle_model_name"
                            @update:modelValue="(v) => setField('vehicle_model_name', v)"
                        />
                    </div>
                    <div class="input-box w-[30%]">
                        <inputbox :inputid="`${idPrefix}_fuel_type`" :errmsg="validationMessage.fuel_type" :readonly="true" label="유종" :modelValue="model.fuel_type" @update:modelValue="(v) => setField('fuel_type', v)" />
                    </div>
                </div>

                <div v-if="!isHidden('pickup_fuel_gauge') || !isHidden('pickup_mileage')" class="section-row items-center">
                    <div class="input-box w-[39.5%]">
                        <inputbox
                            :inputid="`${idPrefix}_pickup_fuel_gauge`"
                            :number="'n'"
                            :placeholder="'배차 유류량을 입력하세요.'"
                            :unit="'%'"
                            :errmsg="validationMessage.pickup_fuel_gauge"
                            label="배차 유류량"
                            :maxlength="3"
                            :readonly="isFieldReadonly('pickup_fuel_gauge')"
                            :modelValue="model.pickup_fuel_gauge"
                            @update:modelValue="(v) => setField('pickup_fuel_gauge', v)"
                        />
                    </div>
                    <div class="input-box w-[60%]">
                        <inputbox
                            :inputid="`${idPrefix}_pickup_mileage`"
                            :number="'n'"
                            :placeholder="'배차 시 KM를 입력하세요.'"
                            :unit="'KM'"
                            :errmsg="validationMessage.pickup_mileage"
                            label="배차 시 KM"
                            :maxlength="7"
                            :readonly="isFieldReadonly('pickup_mileage')"
                            :modelValue="model.pickup_mileage"
                            @update:modelValue="(v) => setField('pickup_mileage', v)"
                        />
                    </div>
                </div>
            </div>

            <div class="section-right">
                <div class="section-row">
                    <div class="input-box w-[100%]">
                        <garage
                            :inputid="`${idPrefix}_garage_id`"
                            :required="isEffectivelyRequired('garage_id')"
                            :placeholder="'지점을 선택하세요'"
                            :readonly="isFieldReadonly('garage_id')"
                            :errmsg="validationMessage.garage_id"
                            label="지점"
                            :modelValue="model.garage_id"
                            @update:modelValue="(v) => setField('garage_id', v)"
                        />
                    </div>
                </div>

                <div class="section-row">
                    <div class="input-box w-[30%]">
                        <delivery
                            :inputid="`${idPrefix}_pickup_delivery_id`"
                            :placeholder="'행정구역을 선택하세요.'"
                            :readonly="isFieldReadonly('pickup_delivery_id')"
                            :padding="'0.5rem'"
                            label="딜리버리(인수)"
                            :modelValue="model.pickup_delivery_id"
                            @update:modelValue="(v) => setField('pickup_delivery_id', v)"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <addresscomponent
                            :inputid="`${idPrefix}_pickup_delivery_address`"
                            :readonly="isFieldReadonly('pickup_delivery_address') || isPickupAddressReadonly"
                            label="인수장소"
                            :errmsg="validationMessage.pickup_delivery_address"
                            :required="isPickupAddressRequired"
                            :modelValue="model.pickup_delivery_address"
                            @update:modelValue="(v) => setField('pickup_delivery_address', v)"
                            @addressCompleted="(payload) => handleAddressCompleted('pickup_delivery', payload)"
                        />
                    </div>
                    <div class="input-box w-[40%]">
                        <inputbox
                            :inputid="`${idPrefix}_pickup_delivery_address_detail`"
                            :placeholder="'상세주소를 입력하세요.'"
                            :errmsg="validationMessage.pickup_delivery_address_detail"
                            :readonly="isFieldReadonly('pickup_delivery_address_detail') || isPickupAddressReadonly"
                            :modelValue="model.pickup_delivery_address_detail"
                            @update:modelValue="(v) => setField('pickup_delivery_address_detail', v)"
                        />
                    </div>
                </div>

                <div class="section-row">
                    <div class="input-box w-[30%]">
                        <delivery
                            :inputid="`${idPrefix}_return_expected_delivery_id`"
                            :placeholder="'행정구역을 선택하세요.'"
                            :readonly="isFieldReadonly('return_expected_delivery_id')"
                            label="딜리버리(반납)"
                            :modelValue="model.return_expected_delivery_id"
                            @update:modelValue="(v) => setField('return_expected_delivery_id', v)"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <addresscomponent
                            :inputid="`${idPrefix}_return_delivery_address`"
                            :readonly="isFieldReadonly('return_delivery_address') || isReturnAddressReadonly"
                            label="반납장소"
                            :errmsg="validationMessage.return_delivery_address"
                            :required="isReturnAddressRequired"
                            :modelValue="model.return_delivery_address"
                            @update:modelValue="(v) => setField('return_delivery_address', v)"
                            @addressCompleted="(payload) => handleAddressCompleted('return_delivery', payload)"
                        />
                    </div>
                    <div class="input-box w-[40%]">
                        <inputbox
                            :inputid="`${idPrefix}_return_delivery_address_detail`"
                            :placeholder="'상세주소를 입력하세요.'"
                            :errmsg="validationMessage.return_delivery_address_detail"
                            :readonly="isFieldReadonly('return_delivery_address_detail') || isReturnAddressReadonly"
                            :modelValue="model.return_delivery_address_detail"
                            @update:modelValue="(v) => setField('return_delivery_address_detail', v)"
                        />
                    </div>
                </div>

                <div v-if="!isHidden('return_fuel_gauge') || !isHidden('return_mileage')" class="section-row items-center">
                    <div class="input-box w-[24.7%]">
                        <inputbox
                            :inputid="`${idPrefix}_return_fuel_gauge`"
                            :number="'n'"
                            :placeholder="'반납 유류량을 입력하세요.'"
                            :unit="'%'"
                            :errmsg="validationMessage.return_fuel_gauge"
                            label="반납 유류량"
                            :maxlength="3"
                            :readonly="isFieldReadonly('return_fuel_gauge')"
                            :modelValue="model.return_fuel_gauge"
                            @update:modelValue="(v) => setField('return_fuel_gauge', v)"
                        />
                    </div>
                    <div class="input-box w-[75.3%]">
                        <inputbox
                            :inputid="`${idPrefix}_return_mileage`"
                            :number="'n'"
                            :placeholder="'반납 시 KM를 입력하세요.'"
                            :unit="'KM'"
                            :errmsg="validationMessage.return_mileage"
                            label="반납 시 KM"
                            :maxlength="7"
                            :readonly="isFieldReadonly('return_mileage')"
                            :required="isEffectivelyRequired('return_mileage')"
                            :modelValue="model.return_mileage"
                            @update:modelValue="(v) => setField('return_mileage', v)"
                        />
                    </div>
                </div>
            </div>
        </div>
        <div class="section-body">
            <div class="input-box w-full">
                <multiuploadbox
                    :inputid="`${idPrefix}_vehicle_image`"
                    :modelValue="vehicleImageFromAttachments"
                    :placeholder="'차량을 선택하세요'"
                    :errmsg="validationMessage.vehicle_image"
                    label="차량이미지"
                    :category="'customer_accident_vehicle'"
                    :readonly="isFieldReadonly('vehicle_image')"
                    :required="isEffectivelyRequired('vehicle_image')"
                    @update:modelValue="(v) => setField('vehicle_image', v)"
                />
            </div>
        </div>
    </div>
</template>

<style src="@/assets/css/form-common.css"></style>
